import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Repeat, RefreshCw, Fuel, Wallet, Landmark, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { money, dateFmt, shortAddr } from "@/lib/format";

const BalanceCard = ({ icon: Icon, label, address, main, sub, accent, testid }) => (
  <div className="glass rounded-2xl p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
      <Icon className={`w-5 h-5 text-${accent}-400`} />
    </div>
    <div className="font-mono-fin text-2xl font-bold">{main}</div>
    {sub && <div className="font-mono-fin text-xs text-slate-500 mt-1">{sub}</div>}
    <div className="font-mono-fin text-[10px] text-slate-600 mt-2">{address ? shortAddr(address) : "not set"}</div>
  </div>
);

export default function AdminSweeps() {
  const [bal, setBal] = useState(null);
  const [rows, setRows] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [sweeping, setSweeping] = useState(null);
  const [dbgUser, setDbgUser] = useState("");
  const [dbg, setDbg] = useState(null);
  const [dbgLoading, setDbgLoading] = useState(false);

  const load = async () => {
    const [b, s] = await Promise.all([api.get("/admin/wallet-balances"), api.get("/admin/sweeps")]);
    setBal(b.data);
    setRows(s.data);
  };
  useEffect(() => {
    load().catch(() => toast.error("Load failed"));
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const scanNow = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/scan-deposits");
      toast.success(data.enabled ? `Scanned ${data.scanned} wallets · ${data.credited} new` : "Auto-detect is disabled in Settings");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setScanning(false);
  };

  const sweepNow = async (id) => {
    setSweeping(id);
    try {
      const { data } = await api.post(`/admin/sweeps/${id}/sweep`);
      toast.success(data.credited ? "Swept & credited to user" : "Processed");
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setSweeping(null);
  };

  const runDebug = async () => {
    if (!dbgUser.trim()) { toast.error("Enter a username or email"); return; }
    setDbgLoading(true); setDbg(null);
    try {
      const { data } = await api.get(`/admin/deposit-debug?username=${encodeURIComponent(dbgUser.trim())}`);
      setDbg(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setDbgLoading(false);
  };

  const b = bal?.balances || {};

  return (
    <div className="space-y-6" data-testid="admin-sweeps-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl font-bold flex items-center gap-2"><Repeat className="w-6 h-6 text-amber-400" /> Deposit Sweeps</h1>
        <button data-testid="sweeps-scan-btn" onClick={scanNow} disabled={scanning} className="gold-btn px-5 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 disabled:opacity-60">
          <RefreshCw className={`w-4 h-4 ${scanning ? "animate-spin" : ""}`} /> {scanning ? "Scanning..." : "Scan Now"}
        </button>
      </div>

      {bal && !bal.configured && (
        <div className="rounded-2xl bg-amber-500/10 border border-amber-500/20 p-4 flex gap-3 text-sm text-amber-200">
          <AlertTriangle className="w-5 h-5 shrink-0" /> No wallets configured yet. Set Fee, Collection and Withdrawal wallets in Admin → Settings to enable auto-sweep and see balances.
        </div>
      )}
      {bal?.error && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/20 p-4 text-sm text-red-300">Balance lookup error: {bal.error}</div>
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        <BalanceCard testid="fee-balance" icon={Fuel} accent="sky" label="Fee / Gas Wallet" address={b.fee?.address}
          main={b.fee ? `${Number(b.fee.bnb).toFixed(4)} BNB` : "—"} sub="gas for sweeps" />
        <BalanceCard testid="collection-balance" icon={Landmark} accent="emerald" label="Collection Wallet" address={b.collection?.address}
          main={b.collection ? money(b.collection.usdt) : "—"} sub={b.collection ? `${Number(b.collection.bnb).toFixed(4)} BNB` : "USDT collected"} />
        <BalanceCard testid="withdrawal-balance" icon={Wallet} accent="amber" label="Withdrawal Wallet" address={b.withdrawal?.address}
          main={b.withdrawal ? money(b.withdrawal.usdt) : "—"} sub={b.withdrawal ? `${Number(b.withdrawal.bnb).toFixed(4)} BNB` : "payout balance"} />
      </div>

      <div className="glass rounded-2xl p-6" data-testid="deposit-debug-card">
        <h3 className="font-display font-bold text-lg mb-1">Deposit Detection Debug</h3>
        <p className="text-xs text-slate-400 mb-4">Not seeing a deposit? Enter the user's username to check the on-chain scan for their address.</p>
        <div className="flex gap-3">
          <input data-testid="debug-username-input" value={dbgUser} onChange={(e) => setDbgUser(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runDebug()} placeholder="username or email"
            className="flex-1 bg-[#08080C] border border-amber-500/20 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-500/50" />
          <button data-testid="debug-run-btn" onClick={runDebug} disabled={dbgLoading}
            className="gold-btn px-5 py-2.5 rounded-xl font-bold text-sm disabled:opacity-60">
            {dbgLoading ? "Checking…" : "Check"}
          </button>
        </div>
        {dbg && (
          <div className="mt-4 space-y-3" data-testid="debug-result">
            {dbg.error ? (
              <div className="rounded-xl bg-red-500/10 border border-red-500/25 p-4 text-sm text-red-300">{dbg.error}</div>
            ) : (
              <>
                <div className={`rounded-xl p-4 text-sm border ${dbg.usdt_balance >= 0.01 ? "bg-emerald-500/10 border-emerald-500/25 text-emerald-200" : "bg-amber-500/10 border-amber-500/25 text-amber-200"}`}>
                  {dbg.note}
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                  <div className="bg-[#08080C] border border-amber-500/15 rounded-lg p-3"><div className="text-slate-500 uppercase">USDT on address</div><div className="font-mono-fin gold-text text-lg mt-1">{money(dbg.usdt_balance)}</div></div>
                  <div className="bg-[#08080C] border border-amber-500/15 rounded-lg p-3"><div className="text-slate-500 uppercase">BNB (gas)</div><div className="font-mono-fin text-slate-200 mt-1">{Number(dbg.bnb_balance).toFixed(5)}</div></div>
                  <div className="bg-[#08080C] border border-amber-500/15 rounded-lg p-3"><div className="text-slate-500 uppercase">Auto-detect</div><div className="font-mono-fin text-slate-200 mt-1">{String(dbg.auto_detect_enabled)}</div></div>
                  <div className="bg-[#08080C] border border-amber-500/15 rounded-lg p-3"><div className="text-slate-500 uppercase">Sweep ready</div><div className="font-mono-fin text-slate-200 mt-1">{String(dbg.sweep_ready)}</div></div>
                </div>
                <div className="font-mono-fin text-[11px] text-slate-500 break-all">Deposit address: {dbg.deposit_address}</div>
                {dbg.recent_deposits?.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs min-w-[560px]">
                      <thead><tr className="text-amber-400/80 uppercase border-b border-amber-500/15"><th className="text-left py-2">Amount</th><th className="text-left py-2">Swept</th><th className="text-left py-2">Credited</th><th className="text-left py-2">Error</th></tr></thead>
                      <tbody>
                        {dbg.recent_deposits.map((t, i) => (
                          <tr key={i} className="border-b border-slate-800/40">
                            <td className="py-2 font-mono-fin gold-text">{money(t.amount)}</td>
                            <td className="py-2">{t.swept ? <span className="text-emerald-400">yes</span> : <span className="text-slate-600">no</span>}</td>
                            <td className="py-2">{t.credited ? <span className="text-emerald-400">yes</span> : <span className="text-slate-600">no</span>}</td>
                            <td className="py-2 text-red-300/80 break-words max-w-[220px]">{t.sweep_error || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="glass rounded-2xl p-6 overflow-x-auto">
        <h3 className="font-display font-bold text-lg mb-4">Auto-Detected Deposits & Sweep Status</h3>
        <table className="w-full text-sm min-w-[720px]">
          <thead><tr className="text-amber-400 uppercase text-xs tracking-wider border-b border-amber-500/20">
            <th className="text-left py-3">User</th><th className="text-left py-3">Amount</th><th className="text-left py-3">Deposit Tx</th>
            <th className="text-left py-3">Credited</th><th className="text-left py-3">Sweep</th><th className="text-right py-3">Detected</th><th className="text-right py-3">Action</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-800/50" data-testid={`sweep-row-${r.id}`}>
                <td className="py-3 text-slate-300">{r.user_email}</td>
                <td className="py-3 font-mono-fin gold-text font-bold">{money(r.amount)}</td>
                <td className="py-3 font-mono-fin text-xs text-sky-400">{shortAddr(r.txid)}</td>
                <td className="py-3">
                  {r.credited
                    ? <span className="text-xs px-2 py-1 rounded bg-emerald-500/15 text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> credited</span>
                    : <span className="text-xs px-2 py-1 rounded bg-amber-500/15 text-amber-400 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> pending</span>}
                </td>
                <td className="py-3">
                  {r.swept
                    ? <span className="text-xs px-2 py-1 rounded bg-emerald-500/15 text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {r.sweep_txid === "manual" ? "manual" : shortAddr(r.sweep_txid)}</span>
                    : r.sweep_error
                      ? <div className="max-w-[260px]">
                          <span className="text-xs px-2 py-1 rounded bg-red-500/15 text-red-400">failed</span>
                          <div data-testid={`sweep-error-${r.id}`} className="text-[10px] text-red-300/80 mt-1 break-words leading-tight">{r.sweep_error}</div>
                        </div>
                      : <span className="text-xs px-2 py-1 rounded bg-amber-500/15 text-amber-400 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> pending</span>}
                </td>
                <td className="py-3 text-right text-slate-500 text-xs">{dateFmt(r.created_at)}</td>
                <td className="py-3 text-right">
                  {!r.credited && (
                    <button data-testid={`sweep-credit-${r.id}`} onClick={() => sweepNow(r.id)} disabled={sweeping === r.id}
                      className="text-xs px-3 py-1.5 rounded-lg gold-btn font-bold disabled:opacity-60">
                      {sweeping === r.id ? "..." : "Sweep & Credit"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-500">No auto-detected deposits yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
